import type { ReactElement } from 'react';
import type { RuleSet } from 'styled-components';

import { useStats } from 'react-instantsearch';
import styled, { css } from 'styled-components';

import {
  PRODUCT_LIST_TOTAL_RESULTS_VARIABLE,
  SEARCH_TERM,
} from '../../../utilities/constants/amplienceVariables';
import { useProductListContext } from '../../../utilities/context/static/ProductListContext';
import { useAlgoliaLoadingState } from '../../../utilities/hooks/search/useAlgoliaLoadingState';
import { Title } from '../../productList/title/Title';
import { Skeleton } from '../../shared/common/SkeletonPlaceholder';
import { media } from '../../shared/core/styles';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const styles = {
  GStarListerTitleNoResults: css<{ $isSearchNoResult: boolean }>``,
  AafListerTitleNoResults: css<{ $isSearchNoResult: boolean }>`
    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      padding: 64px 0 16px;
    }
  `,
};

const ListerTitleNoResultsStyle: Record<SiteType, RuleSet<{ $isSearchNoResult: boolean }>> = {
  [SiteType.Gstar]: styles.GStarListerTitleNoResults,
  [SiteType.Wahts]: styles.GStarListerTitleNoResults,
  [SiteType.Outlet]: styles.GStarListerTitleNoResults,
  [SiteType.Employeeshop]: styles.GStarListerTitleNoResults,
  [SiteType.Aaf]: styles.AafListerTitleNoResults,
};

export interface TitleProps {
  isSearchNoResult: boolean;
}

const S = {
  Container: styled.div`
    ${({ theme }) => css`
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      background-color: ${theme.colors.background.base};

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          align-items: center;
        `,
      })}
    `}
  `,
  SearchTitle: styled(Title)<{ $isSearchNoResult: boolean }>`
    ${({ theme }) => css`
      padding: ${theme.padding.lg} ${theme.padding['2xs']} 0;
      font-size: ${theme.font.size[4]};
      font-weight: ${theme.font.weight.bold};
      overflow-wrap: break-word;
      max-width: 100%;

      @media ${media(theme).greaterThan('sm')} {
        padding: ${theme.padding.lg} ${theme.padding.sm} 0;
      }

      @media ${media(theme).greaterThan('lg')} {
        padding: ${theme.padding.lg} ${theme.padding.md} 0;
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          font-weight: 700;
          letter-spacing: 0.8px;
          font-family: ${theme.font.family.primary};

          @media ${media(theme).greaterThan('sm')} {
            padding: 24px 0 16px;
          }
        `,
      })}
    `}

    ${({ $isSearchNoResult, theme }) =>
      $isSearchNoResult && ListerTitleNoResultsStyle[theme.siteType]}
  `,
  SubTitle: styled.p`
    ${({ theme }) => css`
      line-height: 41px;
      font-size: ${theme.font.size[4]};
      color: ${({ theme }) => theme.colors.content.secondary};
      padding: ${theme.padding.sm} ${theme.padding['2xs']} 0;

      @media ${media(theme).greaterThan('sm')} {
        padding: ${theme.padding.xl} ${theme.padding.sm} 0;
      }

      @media ${media(theme).greaterThan('lg')} {
        padding: ${theme.padding.xl} ${theme.padding.md} 0;
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          font-size: 14px;
          font-weight: 400;
          line-height: 136%;
          letter-spacing: 0.14px;
          color: #000;
          font-family: ${theme.font.family.primary};
          padding: 0;

          @media ${media(theme).lessThan('sm')} {
            display: none;
          }

          @media ${media(theme).greaterThan('sm')} {
            padding: 0;
          }
        `,
      })}
    `}
  `,
};

export const ListerTitle = ({ isSearchNoResult }: TitleProps): ReactElement => {
  const { isFirstLoading } = useAlgoliaLoadingState();
  const { nbHits, query = '' } = useStats();
  const { systemTexts } = useProductListContext();

  const searchTitle = (systemTexts?.searchNoResult?.resultsFor || '')
    ?.replace(PRODUCT_LIST_TOTAL_RESULTS_VARIABLE, nbHits.toString())
    ?.replace(SEARCH_TERM, `'${query}'`);

  return (
    <S.Container>
      <S.SearchTitle $isSearchNoResult={isSearchNoResult} dataTestId="page-title">
        <Skeleton width="250px" when={isFirstLoading}>
          {searchTitle}
        </Skeleton>
      </S.SearchTitle>

      {isSearchNoResult && (
        <S.SubTitle>
          {nbHits} {systemTexts?.totalNumberOfProducts?.label || ''}
        </S.SubTitle>
      )}
    </S.Container>
  );
};
